import styled from 'styled-components';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { Button } from '../../../styles/StudentKeyLookupStyles';
import {
  overlayBackgroundFor,
  OverlayStyle,
} from '../shared/protectedImage/overlayStyles';

export const Wrapper = styled.div`
  padding: 1em 0;
`;

// The yearbook confirmation dialogs are raw HTML strings handed to SweetAlert,
// so the React <ImageProtectionOverlay> cannot be rendered into them. This
// builds the same background the shared overlay registry would produce for
// the studio's chosen style, inlined. `.img-wrapper` (default.scss) is already
// `position: relative` and its height comes from the in-flow pose <img> (the
// background layer is absolutely positioned), so an inset-0 child matches the
// pose image box exactly. Only emit it when protection is enabled.
//
// `split_blur` has no raw-HTML equivalent (it needs a duplicated, blurred
// <img>, which a React component would render), so it degrades to the flat
// scrim background here rather than silently downgrading to a vignette.
export const protectionOverlayHtml = (style: OverlayStyle): string => {
  const background = overlayBackgroundFor(
    style === 'split_blur' ? 'scrim' : style,
  );
  return (
    '<div aria-hidden="true" data-testid="image-protection-overlay" ' +
    'style="position:absolute;top:0;right:0;bottom:0;left:0;pointer-events:none;' +
    `z-index:2;background:${background};` +
    '"></div>'
  );
};

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(228px, 1fr));
  gap: 1em;
  @media screen and (max-width: 500px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }
`;

// `background` is the URL of the backdrop shown behind a pose cut-out.
export const Image = styled.img<{ background?: string | null }>`
  width: 100%;
  display: block;
  height: auto;
  background: ${({ background }) =>
    background ? `url("${background}")` : 'none'};
  background-size: contain;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

export const ImageButton = styled(Button)`
  position: absolute;
  bottom: 25px;
  left: 25px;
  right: 25px;
  /* Above the image-protection vignette (z-index 2), which is darkest exactly
     where this button sits. */
  z-index: 3;
  display: none;
  font-family: 'Inter', sans-serif;
  @media screen and (max-width: 500px) {
    left: 0;
    right: 0;
    bottom: 0;
    display: block;
    height: 36px;
    font-size: 16px;
    padding: 0 13px;
    font-weight: 500;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
  }
`;

export const ZoomIcon = styled(MagnifyingGlassIcon)`
  color: white;
  position: absolute;
  width: 45px;
  height: 45px;
  top: 15px;
  right: 15px;
  /* Above the image-protection vignette (z-index 2). */
  z-index: 3;
  display: none;
  cursor: pointer;
  border-radius: 3px;
  padding: 2px;
  background-color: #0b76b7;
  @media screen and (max-width: 500px) {
    width: 24px;
    height: 24px;
    top: 4px;
    right: 4px;
    display: block;
  }
`;

export const ImageWrapper = styled.div<{ selected?: boolean }>`
  position: relative;
  /* The protection overlay is absolutely positioned to this box, so it has to
     hug the image exactly. As a direct grid item it would otherwise stretch to
     the tallest tile in the row and the vignette would spill below the image. */
  align-self: flex-start;
  border-width: 6px;
  border-style: solid;
  border-color: transparent;
  &:hover {
    border-color: #0b76b7;
    border-width: 6px;
    ${ZoomIcon}, ${ImageButton} {
      display: block;
    }
  }
  &.selected {
    border-color: #0b76b7;
    border-width: 6px;
  }
  @media screen and (max-width: 500px) {
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
    overflow: hidden;
  }
`;

export const CarouselImgWrapper = styled.div`
  height: 100%;
  display: inline-flex;
  flex-direction: column;
  justify-content: space-between;
`;

/* Positioning context for the protection overlay inside the zoom carousel.
   Takes over the sizing CarouselImg used to do itself (stretched to the flex
   line's cross size, height minus the button row) so the overlay box and the
   image box are one and the same. */
export const CarouselImgContainer = styled.div`
  position: relative;
  height: calc(100% - 55px);
  line-height: 0;
`;

export const CarouselImg = styled(Image)`
  height: 100%;
  width: 100%;
  display: block;
  object-fit: cover;
`;

export const ButtonRow = styled.div`
  margin: 1em 0;
  display: flex;
  gap: 10px;
`;

export const SubHeader = styled.h2`
  font-size: 48px;
  font-weight: bold;
  line-height: 58px;
  color: #111928;
  font-family: 'Inter', sans-serif;
`;

export const CategoryWrapper = styled.div`
  margin-bottom: 64px;
`;

export const BlueSubHeader = styled(SubHeader)`
  color: #327ab7;
`;
